// M4-A shale player actions (re-exported through src/core/actions.js). Each returns { ok, msg, ...extra }.
import { M4_FROM } from '../../data/m4/rules.js';
import { yf, eqDef } from '../defs.js';
import { sh, ok, no } from './shaleUtil.js';
import { planLateralImpl, steerImpl } from './shaleLaterals.js';
import { buildPadImpl } from './shalePads.js';
import { setCompletionDesignImpl, fracWellImpl, deferFracImpl, refracImpl } from './shaleFrac.js';
import { contractFracServiceImpl, releaseFracServiceImpl, buildWaterFacilityImpl, contractWaterServiceImpl, releaseWaterImpl } from './shaleServices.js';
import { shaleBuyRoute } from './shale.js';
import * as core from '../../core/actions.js';

const W = (state, id) => state.wells[id];
const opWell = (w) => (w?.ownerOperatorId ? no(`${w.name} belongs to the operator.`) : null);

// §1 laterals: plan before the kickoff point (bench, length, optional lateral spacing for downspacing tests).
export const planLateral = (state, wellId, opts = {}) => opWell(W(state, wellId)) ?? planLateralImpl(state, W(state, wellId), opts);
// Manual geosteering: 'up' | 'down' | 'hold' | 'auto' (a geosteerer steers on 'auto').
export const steer = (state, wellId, dir) => steerImpl(state, W(state, wellId), dir);
// §4 drill the next pad slot: assign a rig to a shale plot and plan its lateral in one step.
export function drillPadWell(state, rigUid, fieldId, plotIdx, opts = {}) {
  if (yf(state) < M4_FROM) return no('Pad drilling is a shale-era practice.');
  const r = core.assignRig(state, rigUid, fieldId, plotIdx, { target: opts.target });
  if (!r.ok) return r;
  const well = state.wells[state.rigs[rigUid]?.wellId];
  if (!well || (!opts.bench && !opts.lengthFt && !opts.spacingFt)) return { ...r, wellId: well?.id ?? null };
  const p = planLateralImpl(state, well, { bench: opts.bench ?? well.lateral?.bench, lengthFt: opts.lengthFt ?? well.lateral?.targetFt, spacingFt: opts.spacingFt });
  return { ok: true, msg: `${r.msg} ${p.msg}`, wellId: well.id, planned: p.ok };
}

// Build a pad ahead of the rig ($400k 2014 $, 21 days); the first well on an unbuilt section waits for it.
export const buildPad = (state, fieldId, plotIdx) => buildPadImpl(state, fieldId, plotIdx);

// §2 completion design & the frac job
export const setCompletionDesign = (state, wellId, design) => opWell(W(state, wellId)) ?? setCompletionDesignImpl(state, W(state, wellId), design);
export const fracWell = (state, wellId, design = null) => opWell(W(state, wellId)) ?? fracWellImpl(state, W(state, wellId), design);
export const deferFrac = (state, wellId) => deferFracImpl(state, W(state, wellId));
export const refracWell = (state, wellId) => opWell(W(state, wellId)) ?? refracImpl(state, W(state, wellId));

// §5 services & logistics
export const contractFracService = (state, defId, opts = {}) => contractFracServiceImpl(state, defId, opts ?? {});
export const releaseFracService = (state, uid) => releaseFracServiceImpl(state, uid);
export const buildWaterFacility = (state, defId, fieldId) => buildWaterFacilityImpl(state, defId, fieldId);
export const contractWaterService = (state, defId, fieldId, opts = {}) => contractWaterServiceImpl(state, defId, fieldId, opts ?? {});
export const releaseWaterFacility = (state, uid) => releaseWaterImpl(state, uid);
export function buyProppant(state, defId, tons, opts = {}) {
  const def = eqDef(defId);
  if (!def || def.stats?.service !== 'proppant') return no('Not a proppant.');
  return shaleBuyRoute(state, def, tons, opts) ?? no('Proppant is sold from 2000.');
}
// Convenience automation for shale ops: { sand, water, disposal, frac, lift } booleans (all on by default).
export function setShaleAuto(state, patch = {}) {
  if (yf(state) < M4_FROM) return no('Shale operations start in 2000.');
  const a = sh(state).auto;
  for (const k of ['sand', 'water', 'disposal', 'frac', 'lift']) if (patch[k] !== undefined) a[k] = !!patch[k];
  return ok(`Shale automation: ${Object.entries(a).map(([k, v]) => `${k} ${v ? 'on' : 'off'}`).join(', ')}.`);
}
